/**
 * Azure Translator / GPT learning target codes (extension azure.json parity).
 * Dual/Google UI may still use zh-CN / zh-TW — map before GPT learning APIs.
 */
import azureJson from '../../../extension/src/adapters/resource/json/azure.json';

export type AzureTranslationEntry = {
  name: string;
  nativeName: string;
  dir?: string;
};

export type LearningTargetLanguageEntry = {
  code: string;
  /** Extension popup label: `${name} (${nativeName})`. */
  label: string;
};

const ZH_HANS_ALIASES = new Set([
  'zh-hans',
  'zh-cn',
  'zh-sg',
  'zh-hans-cn',
  'zh-hans-sg',
]);

const ZH_HANT_ALIASES = new Set([
  'zh-hant',
  'zh-tw',
  'zh-hk',
  'zh-mo',
  'zh-hant-tw',
  'zh-hant-hk',
  'zh-hant-mo',
]);

const azureTranslation = azureJson.translation as Record<string, AzureTranslationEntry>;

/**
 * Full learning target list — same source as extension
 * `initLearningModeTargetLanguageOptions` (popupApp.js).
 */
export function learningTargetLanguageEntries(): LearningTargetLanguageEntry[] {
  return Object.keys(azureTranslation).map((code) => {
    const obj = azureTranslation[code];
    return {
      code,
      label: `${obj.name} (${obj.nativeName})`,
    };
  });
}

export function isAzureTranslationCode(code: string | undefined | null): boolean {
  return Boolean(code && Object.prototype.hasOwnProperty.call(azureTranslation, code));
}

/**
 * Normalize a UI / stored language code to an Azure translation catalog key.
 * - Chinese: zh-CN → zh-Hans, zh-TW → zh-Hant
 * - Others: primary subtag (en-US → en), empty if unknown bare `zh`
 */
export function toAzureTranslationCode(code: string | undefined | null): string {
  const raw = String(code || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
  if (!raw || raw === 'null' || raw === 'custom') return '';

  if (raw === 'zh') return '';
  if (ZH_HANS_ALIASES.has(raw) || raw.startsWith('zh-hans')) return 'zh-Hans';
  if (ZH_HANT_ALIASES.has(raw) || raw.startsWith('zh-hant')) return 'zh-Hant';
  if (raw.startsWith('zh-')) {
    const rest = raw.slice(3);
    if (rest === 'cn' || rest === 'sg') return 'zh-Hans';
    if (rest === 'tw' || rest === 'hk' || rest === 'mo') return 'zh-Hant';
    return '';
  }

  if (code === 'zh-Hans' || code === 'zh-Hant') return code;

  const primary = raw.split('-')[0] || '';
  return primary;
}

/**
 * Migrate legacy learningModeTargetLanguage values to Azure keys.
 * Empty stays empty (extension: user must pick a target).
 */
export function migrateLearningTargetLanguage(code: string | undefined | null): string {
  if (!code) return '';
  if (isAzureTranslationCode(code)) return code;
  const mapped = toAzureTranslationCode(code);
  if (mapped && isAzureTranslationCode(mapped)) return mapped;
  return '';
}
